import { useCallback, useState, type ReactNode } from 'react';
import type { Operaciones, Yo } from '../api/operaciones';
import type { Partner } from '../api/ops/partners';
import { Boton } from '../componentes/Boton';
import { EstadoCargando, EstadoError, Tarjeta } from '../componentes/EstadoPagina';
import { correoDeSoporte } from '../config';
import { Icono } from '../componentes/Icono';
import { estadoDeError } from '../logica/estados';
import { iniciales, type Menu } from '../logica/navegacion';
import { ContextoCabecera, SIN_HERRAMIENTAS, type Herramientas } from '../marco/cabecera';
import { MarcoMovil } from '../marco/MarcoMovil';
import { MarcoOrdenador } from '../marco/MarcoOrdenador';
import type { Vista } from '../marco/PanelNegocio';
import { useEsMovil } from '../marco/tamano';
import { ProveedorDosPasos } from '../marco/VentanaDosPasos';
import { Cuenta } from '../pantallas/cuenta/Cuenta';
import type { PropsPantalla } from '../pantallas/tipos';
import { PermisosContexto, permisosDe } from '../permisos/permisos';
import { RUTA_PORTAL_PARTNER } from '../rutas';
import { SIN_CAMPANA, menuDelPortal, useCarga, type DatosPortal } from './carga';
import { Comisiones } from './Comisiones';
import { DatosFiscales } from './DatosFiscales';
import { SECCIONES, claveMes, mesDe, partnerDesde, porcentaje, seccionDe, type Seccion } from './logica';
import { Inicio, Negocios, TuEnlace } from './Secciones';
import './partners.css';

/**
 * **Portal de partners** (1.23, `diseno/plataforma/1.23-portal-y-solicitud-de-partners.html`): la zona de la cuenta de
 * partner, fuera de un negocio, con el «marco de portal» (componentes.md §17: el marco 0.1 sin campana ni negocios, con
 * «Portal de partners» en la ceja). Menú: Inicio · Negocios · Comisiones (Comisiones · Liquidaciones) · Tu enlace · Datos
 * fiscales; «Mi cuenta» (1.8) en tu cuenta. Las secciones van en `?seccion=` (y `?mes=` en Comisiones). Lee
 * `getPartnerProfile` y, para las secciones, `getPartnerSummary`, `listPartnerBusinesses`, `listPartnerCommissions` (las del
 * mes en curso, filtro `period`, G1098) y `listPartnerSettlements`. El menú es fijo en el panel, sin `getMyNavigation`
 * (G1101: sin negocio no hay navegación de la API). Un partner rechazado o suspendido ve su pantalla (G1100), sin enlace ni
 * cifras.
 */
interface Props {
    ops: Operaciones;
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    onCerrarSesion: () => void;
    onSesionCaducada: () => void;
}

const MENU = menuDelPortal();
/** Sin negocio no hay permisos de negocio: el portal no los mira (la API ya da solo lo del partner). */
const SIN_PERMISOS_DE_NEGOCIO = permisosDe(null);
const ETIQUETAS_MOVIL = new Map(SECCIONES.map((s) => [s.clave, s.corta]));

export function PortalPartner({ ops, ruta, busqueda, navegar, onCerrarSesion, onSesionCaducada }: Props) {
    const movil = useEsMovil();
    const [herramientas, setHerramientas] = useState<Herramientas | null>(null);
    const yo = useCarga<Yo>(() => ops.getMe(), onSesionCaducada);
    const perfil = useCarga<Partner>(() => ops.getPartnerProfile(), onSesionCaducada);
    // Las del mes en curso (hora de Madrid, G1087): Inicio y Negocios buscan en ellas la comisión de cada negocio.
    const comisiones = useCarga(() => ops.listPartnerCommissions({ period: claveMes(mesDe(new Date().toISOString())), limit: 100 }), onSesionCaducada);
    const liquidaciones = useCarga(() => ops.listPartnerSettlements({ limit: 100 }), onSesionCaducada);
    const { seccion, vista: vistaComisiones } = seccionDe(busqueda);

    const ir = useCallback(
        (s: Seccion | 'liquidaciones', extra = '') => {
            navegar(s === 'inicio' && extra === '' ? RUTA_PORTAL_PARTNER : `${RUTA_PORTAL_PARTNER}?seccion=${s}${extra}`);
        },
        [navegar],
    );

    const p = perfil.datos;
    const actual = seccion === 'cuenta' ? (MENU.perfil[0] ?? null) : (MENU.entradas.find((e) => e.clave === seccion) ?? null);
    // En el móvil, la barra de abajo lleva la etiqueta corta (prototipo `NAV`: «Comisión», «Enlace», «Datos»).
    const menu: Menu = movil ? { ...MENU, entradas: MENU.entradas.map((e) => ({ ...e, etiqueta: ETIQUETAS_MOVIL.get(e.clave as Exclude<Seccion, 'cuenta'>) ?? e.etiqueta })) } : MENU;
    const titulo = actual === null ? '' : (MENU.entradas.find((e) => e.clave === actual.clave) ?? actual).etiqueta;

    const contenido = ((): ReactNode => {
        const error = perfil.error ?? yo.error;
        if (error !== null) {
            return <EstadoError seccion={titulo === '' ? 'tu portal' : titulo} codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { perfil.recargar(); yo.recargar(); }} movil={movil} />;
        }
        if (p === null || yo.datos === null) {
            return <EstadoCargando seccion={titulo === '' ? 'tu portal' : titulo} />;
        }
        if (seccion === 'cuenta') {
            const props: PropsPantalla = { ops, negocio: '', yo: yo.datos, membresia: undefined, nombreNegocio: p.legal_name, soloLectura: false, movil, ruta, busqueda, navegar, recargar: yo.recargar, onSesionCaducada };
            return <Cuenta {...props} />;
        }
        if (p.status === 'rejected' || p.status === 'suspended') {
            return <PartnerInactivo estado={p.status} nombre={p.legal_name} movil={movil} />;
        }
        const d: DatosPortal = {
            ops,
            perfil: p,
            pct: porcentaje(p.commission_rate_bp),
            // El enlace de alta (`/crear-cuenta?p=`, G1114) lo da la API con el dominio de cada entorno (G1097); el panel no
            // lo construye. En revisión es nulo (G1108).
            enlace: p.referral_url,
            comisiones,
            liquidaciones,
            movil,
            ruta,
            busqueda,
            ir,
            navegar,
            onSesionCaducada,
            alGuardarPerfil: perfil.poner,
        };
        return (
            <>
                <EstadoDelPartner perfil={p} />
                {seccion === 'inicio' && <Inicio d={d} />}
                {seccion === 'negocios' && <Negocios d={d} />}
                {seccion === 'comisiones' && <Comisiones d={d} vista={vistaComisiones} />}
                {seccion === 'enlace' && <TuEnlace d={d} />}
                {seccion === 'datos' && <DatosFiscales d={d} />}
            </>
        );
    })();

    const v: Vista = {
        movil,
        membresias: [],
        negocioId: '',
        nombreNegocio: p?.legal_name ?? '',
        // El código solo cuando está aprobado: llega con la aprobación y antes es nulo (G1096, G1108, C.57).
        lineaNegocio: p === null ? '' : p.status === 'approved' && p.referral_code !== null ? `Partner · código ${p.referral_code}` : 'Partner',
        inicialesNegocio: iniciales(p?.legal_name ?? ''),
        persona: {
            nombre: yo.datos?.account.display_name ?? '',
            email: yo.datos?.account.email ?? '',
            iniciales: iniciales(yo.datos?.account.display_name ?? ''),
            // «Partner desde…» con `approved_at` en lugar del rol de la persona, que el diseño quita (G1096, C.57).
            rol: partnerDesde(p?.approved_at),
        },
        menu,
        actual,
        titulo: herramientas?.titulo ?? titulo,
        subtitulo: herramientas?.subtitulo ?? null,
        soloLectura: false,
        franja: null,
        campana: SIN_CAMPANA,
        ir: (e) => {
            navegar(e.ruta);
        },
        cambiarNegocio: () => undefined,
        cerrarSesion: onCerrarSesion,
        accionFranja: () => undefined,
        cerrarFranja: () => undefined,
        personalizar: null,
        herramientas: herramientas ?? SIN_HERRAMIENTAS,
        contenido: <ContextoCabecera.Provider value={setHerramientas}>{contenido}</ContextoCabecera.Provider>,
        portal: { ceja: 'Portal de partners', icono: 'handshake' },
    };

    return (
        <PermisosContexto.Provider value={SIN_PERMISOS_DE_NEGOCIO}>
            <ProveedorDosPasos movil={movil}>{movil ? <MarcoMovil v={v} /> : <MarcoOrdenador v={v} />}</ProveedorDosPasos>
        </PermisosContexto.Provider>
    );
}

/** Solicitud aún sin aprobar: lo dice arriba con el texto del prototipo («Solicitud enviada»). */
function EstadoDelPartner({ perfil }: { perfil: Partner }) {
    if (perfil.status !== 'applied') {
        return null;
    }
    return (
        <div role="status" className="rv-pt-estado">
            <span aria-hidden="true">
                <Icono nombre="hourglass" />
            </span>
            <span>
                <strong>Solicitud enviada</strong>
                <span>La revisamos y te avisamos por email. Cuando la aprobemos, tu enlace y tu código empezarán a atribuirte negocios.</span>
            </span>
        </div>
    );
}

/**
 * Partner **rechazado o suspendido** (G1100): qué ha pasado, que sus datos no se pueden cambiar (`updatePartnerProfile` da
 * `409`, G1090) y cómo escribir a Reservaria; sin enlace de alta ni cifras. Sale en todas las secciones menos «Mi cuenta».
 * Hecha con la tarjeta de estado de página (0.4) y sus estilos, sin estética nueva: **se ajustará al llegar C.58** (dibujo y
 * textos de diseño, G1109); los textos y el asunto de ahora son provisionales.
 */
function PartnerInactivo({ estado, nombre, movil }: { estado: 'rejected' | 'suspended'; nombre: string; movil: boolean }) {
    const rechazado = estado === 'rejected';
    const asunto = rechazado ? `Mi solicitud de partner · ${nombre}` : `Mi cuenta de partner suspendida · ${nombre}`;
    return (
        <Tarjeta
            icono={rechazado ? 'circle-x' : 'pause'}
            baldosa="var(--rv-ink)"
            tinta="#fff"
            ceja={rechazado ? 'Solicitud rechazada' : 'Cuenta suspendida'}
            cejaColor="var(--rv-fg-3)"
            titulo={rechazado ? 'No hemos aprobado tu solicitud de partner' : 'Tu cuenta de partner está suspendida'}
            texto="Tu enlace y tu código no atribuyen negocios y tus datos no se pueden cambiar. Si crees que es un error, escríbenos."
            botones={
                <Boton
                    tipo="secundario"
                    icono="mail"
                    movil={movil}
                    onClick={() => {
                        window.location.href = `mailto:${correoDeSoporte()}?subject=${encodeURIComponent(asunto)}`;
                    }}
                >
                    Escribir a Reservaria
                </Boton>
            }
        />
    );
}
